import React, { useState, useEffect, useRef } from 'react';

// --- Interfaces e Tipos ---
interface ArquivoS3 {
    NomeArquivo: string;
    CaminhoCompleto: string;
    TamanhoEmBytes: number;
    DataModificacao: string;
    ThumbnailUrl?: string;
}

interface GerenciadorArquivosProps {
    corporacao: string;
    organizacao: string;
    idEntidade: string | number;
    itemAssociado: string;
    dataAssociacao: string;
    backendUrl: string;
    isActive: boolean;
}

// --- Funções Auxiliares ---
const formatarNomeArquivo = (nomeOriginal: string | null | undefined): string => {
    if (typeof nomeOriginal !== 'string' || !nomeOriginal) return 'Nome de arquivo inválido';
    try {
        const guidPrefixRegex = /^(?:[0-9a-fA-F]{8}-(?:[0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}-)+/;
        const nomeLimpo = nomeOriginal.replace(guidPrefixRegex, '');
        return nomeLimpo || nomeOriginal;
    } catch (e) {
        return nomeOriginal;
    }
};

const formatarData = (dataString: string | null | undefined): string => {
    if (!dataString) return '-';
    try {
        const parteData = dataString.split(' ')[0];
        if (!/^\d{2}\/\d{2}\/\d{4}$/.test(parteData)) return dataString;
        return parteData;
    } catch (e) {
        return dataString;
    }
};

const getIconForFile = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    switch (extension) {
        case 'pdf': return 'file pdf outline';
        case 'doc': case 'docx': return 'file word outline';
        case 'xls': case 'xlsx': return 'file excel outline';
        case 'ppt': case 'pptx': return 'file powerpoint outline';
        case 'jpg': case 'jpeg': case 'png': case 'gif': case 'bmp': case 'svg': return 'file image outline';
        case 'zip': case 'rar': case '7z': return 'file archive outline';
        case 'txt': return 'file alternate outline';
        case 'mp4': case 'mov': case 'avi': case 'wmv': return 'file video outline';
        case 'mp3': case 'wav': case 'ogg': return 'file audio outline';
        default: return 'file outline';
    }
};

const isImage = (fileName: string): boolean => {
    if (!fileName) return false;
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];
    return imageExtensions.includes(extension);
};

// --- Componente ---
const GerenciadorArquivosAssociacoes: React.FC<GerenciadorArquivosProps> = ({
    corporacao,
    organizacao,
    idEntidade,
    itemAssociado,
    dataAssociacao,
    backendUrl,
    isActive,
}) => {
    const [arquivos, setArquivos] = useState<ArquivoS3[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [arquivoParaUpload, setArquivoParaUpload] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);


    const formatarDataParaUrl = (dataBR: string): string => {
        if (!dataBR) return '';
        try {
            if (dataBR.includes('-')) return dataBR;
            const [data, hora] = dataBR.split(' ');
            const [dia, mes, ano] = data.split('/');
            return `${ano}-${mes}-${dia}T${hora}`;
        } catch (e) {
            console.error("Erro ao formatar data para URL:", e);
            return dataBR;
        }
    };

    useEffect(() => {
        const loadData = async () => {
            if (!corporacao || !organizacao || !idEntidade || !itemAssociado || !dataAssociacao) return;

            setIsLoading(true);
            setError(null);

            try {
                // Encode dos parâmetros
                const encCorp = encodeURIComponent(corporacao);
                const encOrg = encodeURIComponent(organizacao);
                const encId = encodeURIComponent(idEntidade.toString());
                const encItem = encodeURIComponent(itemAssociado);
                const dataFormatada = formatarDataParaUrl(dataAssociacao);
                const encData = encodeURIComponent(dataFormatada);

                // Montagem da URL (GET)
                const url = `${backendUrl}/api/arquivos/listar/${encCorp}/${encOrg}/associacao/${encId}/associado/${encItem}/dataassociacao/${encData}`;

                const response = await fetch(url);
                if (!response.ok) throw new Error(`Falha ao buscar arquivos (Status: ${response.status})`);
                
                const data: ArquivoS3[] = await response.json();
                setArquivos(data);
            } catch (err: any) {
                setError(err.message || 'Ocorreu um erro.');
            } finally {
                setIsLoading(false);
            }
        };

        if (isActive) {
            loadData();
        }
    }, [isActive, corporacao, organizacao, idEntidade, itemAssociado, dataAssociacao, backendUrl, refreshTrigger]);

    const handleFileSelect = (files: FileList | null) => {
        if (files && files[0]) {
            setArquivoParaUpload(files[0]);
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
        handleFileSelect(event.dataTransfer.files);
    };

    const handleUploadAreaClick = () => {
        fileInputRef.current?.click();
    };

    const handleUpload = async () => {
        if (!arquivoParaUpload) return;
        
        // Validação extra de segurança
        if (!corporacao || !organizacao || !idEntidade || !itemAssociado || !dataAssociacao) {
            alert("Faltam dados da associação (Corporação, Item ou Data) para realizar o upload.");
            return;
        }

        setIsUploading(true);
        setError(null);
        
        const formData = new FormData();
        formData.append('arquivo', arquivoParaUpload);

        try {
            // Encode dos parâmetros
            const encCorp = encodeURIComponent(corporacao);
            const encOrg = encodeURIComponent(organizacao);
            const encId = encodeURIComponent(idEntidade.toString());
            const encItem = encodeURIComponent(itemAssociado);
            const dataFormatada = formatarDataParaUrl(dataAssociacao);
            const encData = encodeURIComponent(dataFormatada);

            // Montagem da URL (POST)
            const url = `${backendUrl}/api/arquivos/corporacao/${encCorp}/organizacao/${encOrg}/associacao/${encId}/associado/${encItem}/dataassociacao/${encData}`;

            const response = await fetch(url, { method: 'POST', body: formData });
            
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Falha no upload: ${errorText}`);
            }

            alert('Upload realizado com sucesso!');
            setArquivoParaUpload(null);
            setRefreshTrigger(prev => prev + 1);
        } catch (err: any) {
            setError(err.message || 'Ocorreu um erro no upload.');
        } finally {
            setIsUploading(false);
        }
    };

    // --- NOVA FUNÇÃO: APENAS PREVIEW ---
    const handlePreview = async (arquivo: ArquivoS3) => {
        try {
            const url = `${backendUrl}/api/arquivos/preview-url?key=${encodeURIComponent(arquivo.CaminhoCompleto)}`;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Não foi possível gerar o link de preview.');
            }
            const data = await response.json();
            window.open(data.url, '_blank');
        } catch (err: any) {
            alert(err.message || 'Erro ao gerar o preview.');
        }
    };

    // --- NOVA FUNÇÃO: APENAS DOWNLOAD ---
    const handleDownload = async (arquivo: ArquivoS3) => {
        const nomeLimpo = formatarNomeArquivo(arquivo.NomeArquivo);
        // Opcional: Confirmar download
        // if (!window.confirm(`Deseja baixar o arquivo "${nomeLimpo}"?`)) return;

        try {
            const url = `${backendUrl}/api/arquivos/download?key=${encodeURIComponent(arquivo.CaminhoCompleto)}`;
            const response = await fetch(url);
            if (!response.ok) throw new Error('Não foi possível baixar o arquivo.');
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = nomeLimpo;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err: any) {
            alert(err.message || 'Erro ao iniciar o download.');
        }
    };

    const handleDelete = async (arquivo: ArquivoS3) => {
        const nomeLimpo = formatarNomeArquivo(arquivo.NomeArquivo);
        if (!window.confirm(`Tem certeza que deseja deletar o arquivo "${nomeLimpo}"? Esta ação não pode ser desfeita.`)) {
            return;
        }
        try {
            const url = `${backendUrl}/api/arquivos/deletar?key=${encodeURIComponent(arquivo.CaminhoCompleto)}`;
            const response = await fetch(url, { method: 'DELETE' });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Não foi possível deletar o arquivo.');
            }
            alert('Arquivo deletado com sucesso!');
            setRefreshTrigger(prev => prev + 1);
        } catch (err: any) {
            alert(err.message || 'Erro ao deletar o arquivo.');
        }
    };

    return (
        <div className="file-manager-container">
            <div>
                <h4>Adicionar arquivo</h4>
                {!arquivoParaUpload ? (
                    <div 
                        className={`drop-zone ${isDragging ? 'dragging' : ''}`}
                        onClick={handleUploadAreaClick}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <input 
                            ref={fileInputRef}
                            type="file" 
                            onChange={(e) => handleFileSelect(e.target.files)} 
                            style={{ display: 'none' }} 
                        />
                        <div className="drop-zone-text">
                            <i className="upload icon"></i>
                            Arraste e solte o arquivo aqui, ou clique para selecionar
                        </div>
                    </div>
                ) : (
                    <div className="file-preview-container">
                        <div className='file-preview-info'>
                            <p><strong>Arquivo selecionado:</strong> {arquivoParaUpload.name}</p>
                            <p><strong>Tamanho:</strong> {(arquivoParaUpload.size / 1024).toFixed(2)} KB</p>
                        </div>
                        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                            <button onClick={handleUpload} disabled={isUploading} className="upload-button">
                                {isUploading ? 'Enviando...' : 'Confirmar Upload'}
                            </button>
                            <button onClick={() => setArquivoParaUpload(null)} className="ui button secondary small">
                                Cancelar
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <div>
                <h4>Arquivos da associação</h4>
                {isLoading && <div className="loader">Carregando lista de arquivos...</div>}
                {error && <div className="error-message">{error}</div>}
                
                {!isLoading && !error && (
                    <ul className="file-list">
                        {arquivos.length > 0 ? (
                            arquivos.map((arquivo) => (
                                <li key={arquivo.CaminhoCompleto} className="file-list-item">
                                    {/* Comportamento da linha: Imagem -> Preview, Outros -> Download */}
                                    <div 
                                        className="file-info" 
                                        onClick={() => isImage(arquivo.NomeArquivo) ? handlePreview(arquivo) : handleDownload(arquivo)} 
                                        title={isImage(arquivo.NomeArquivo) ? "Clique para ver a imagem" : "Clique para baixar o arquivo"}
                                    >
                                        
                                        {arquivo.ThumbnailUrl ? (
                                            <img src={arquivo.ThumbnailUrl} alt="miniatura" className="file-thumbnail" />
                                        ) : (
                                            <i className={`${getIconForFile(arquivo.NomeArquivo)} icon`}></i>
                                        )}

                                        <div className='file-details'>
                                            <div className="file-name">{formatarNomeArquivo(arquivo.NomeArquivo)}</div>
                                            <div className="file-meta">
                                                <span>{(arquivo.TamanhoEmBytes / 1024).toFixed(2)} KB</span>
                                                <span style={{ margin: '0 5px' }}>•</span>
                                                <span>{formatarData(arquivo.DataModificacao)}</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Botões de Ação */}
                                    <div className="file-actions">
                                        
                                        {/* Botão Preview (olho): Só se for imagem */}
                                        {isImage(arquivo.NomeArquivo) && (
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); handlePreview(arquivo); }} 
                                                className="action-button" 
                                                title="Ver imagem"
                                            >
                                                <i className="eye icon"></i>
                                            </button>
                                        )}

                                        {/* Botão Download: Sempre visível */}
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleDownload(arquivo); }} 
                                            className="action-button" 
                                            title="Baixar arquivo"
                                        >
                                            <i className="download icon"></i>
                                        </button>

                                        {/* Botão Delete */}
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleDelete(arquivo); }} 
                                            className="action-button delete" 
                                            title="Deletar arquivo"
                                        >
                                            <i className="trash alternate outline icon"></i>
                                        </button>

                                    </div>
                                </li>
                            ))
                        ) : (
                            <div className="no-files-message">Nenhum arquivo encontrado para esta associação.</div>
                        )}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default GerenciadorArquivosAssociacoes;